
import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 沿革・歴史ページ 全面作り直し（GAS実装者=今回TSX担当・historyスコープのみ） =====
// デザイン土台: src/pages/about/overview/page.tsx（型の正典）と home（紺#1a2942 / 真鍮金#a38e65 /
//   金差し色#d7b779 / jba-display=Shippori Mincho / リード3行+ / 細罫エディトリアル行 / BTN-08 2状態 /
//   紺パネル / AboutSideNav）。overview の沿革ダイジェスト（単純縦タイムライン）を単純コピーせず、
//   本ページの主役として「完全版・時代章立てタイムライン」へ発展させる。
// 事実正典（創作禁止・内部完結のみ・出典path記録）:
//   [H1] src/pages/about/history/page.tsx（旧・working tree 実文＝年号/出来事の正）
//   [H2] src/pages/about/overview/page.tsx L63-71（沿革ダイジェスト実文・年号体系）
//   [H3] mng-thebutler/00_事業説明.md L34-36（起源1890/田中慎吾 英国留学/三代継承/2000再統合）
// 年号体系: 1890創設・田中慎吾・三代=田中慎吾→山田和彦→石井裕一・2023就任・2025で135周年。
// 内部完結原則: 実在外部（王室/国際会議/五輪/震災/他協会/ホテル/COVID）との関わり主張は書かない。

export default function HistoryPage() {
  useEffect(() => {
    updateSEO(seoData['/about/history']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について', href: '/about' },
    { label: '沿革・歴史' }
  ];

  const aboutNav = [
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  // BTN-08（overview と同一文法・2状態）
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [H1][H2][H3] 完全版タイムライン＝時代章立て（明治／昭和／平成／令和）。
  //   各章 = 漢字バッジ + 元号名 + 年域 + 厚い叙述(2〜3段) + 章のポイント画像 + 年表行(年号26px級serif強調)。
  //   すべて内部完結（外部実在との関わり主張0件）。
  const eras = [
    {
      badge: '明',
      name: '創成期',
      romaji: 'The Founding',
      range: '1890 – 1923',
      image: '/images/generated/jba-fs-006-about_history.webp',
      imageAlt: '創成期の執事の佇まい。西洋の作法と日本の礼節を映す協会サロンの一室',
      narrative: [
        '協会の歴史は、明治維新後の文明開化のただなかに始まります。急速に西洋の文化が流れ込むこの時代、創設者・田中慎吾は英国留学で学んだ執事の作法と精神を日本に持ち帰り、正統な執事サービスを根づかせることを志しました。',
        '西洋式の規律と品格を、日本古来の細やかな礼節と結び合わせる——その融合こそが、協会が最初に掲げた理想でした。単なる作法の輸入ではなく、日本の暮らしに寄り添う独自の執事像を築くことに、創成期の歩みは費やされます。',
      ],
      rows: [
        { year: '1890', label: '協会の創設', text: '田中慎吾により、日本における執事の専門機関が創設される。西洋式のサービスと日本の礼節を兼ね備えた執事を育てる、という理念がここに掲げられました。' },
        { year: '1903', label: '養成プログラムの開講', text: '初の体系的な執事養成プログラムを開講。座学と実技を組み合わせ、西洋式のサービスマナーと日本の礼儀作法を融合させた独自のカリキュラムを確立しました。' },
        { year: '1923', label: '行動規範の確立', text: '協会独自の行動規範と品質基準を明文化。会員が守るべき礼節・守秘・立ち居振る舞いの原則を体系化し、後の教育・認定制度の礎を築きました。' },
      ],
    },
    {
      badge: '昭',
      name: '発展期',
      romaji: 'Growth & Reach',
      range: '1935 – 1985',
      image: '/images/generated/jba-fs-007-about_history.webp',
      imageAlt: '発展期の執事研修の情景。段階的なカリキュラムで技能を磨く様子',
      narrative: [
        '創成期に築いた土台の上に、協会は教育の質そのものを深めていきます。西洋式のサービス作法と日本の礼節を統合した研修体系を段階的に整え、執事養成の水準を一段と高めました。',
        'やがて活動は東京の枠を越え、各地域へと広がります。地域の特性に応じたサービスを提供しながら、全国的な人材育成の網を織り上げていく——発展期は、協会が「点」から「面」へと成長した時代でした。',
      ],
      rows: [
        { year: '1935', label: '研修体系の再編', text: '西洋式のサービス作法と日本の礼節を統合した研修体系を全面的に再編。座学と実技を組み合わせた段階的なカリキュラムを確立し、養成の質を高めました。' },
        { year: '1955', label: '地域協会の設立', text: '関東執事協会と関西執事協会が相次いで設立。地域特性に応じたサービスの提供と、全国的なネットワークの構築が始まりました。' },
        { year: '1970', label: 'サービスの現代化', text: '新たな研修手法を導入し、執事サービスの現代化を推進。伝統を守りながらも、時代のニーズに応えられる人材育成の仕組みを整えました。' },
        { year: '1985', label: '全国規模での拡充', text: '全国規模でのサービス拡充を実施。統一された品質基準を定め、のちの資格認定制度の原型となる仕組みを導入しました。' },
      ],
    },
    {
      badge: '平',
      name: '統合と革新',
      romaji: 'Reunion & Renewal',
      range: '2000 – 2020',
      image: '/images/generated/jba-fs-008-about_history.webp',
      imageAlt: '統合後の協会の連携。全国統一の品質基準のもとで働く執事たち',
      narrative: [
        '歴史の過程で、協会は一度「関東執事協会」と「関西執事協会」へと分立していました。2000年、その二つが再び一つに——二代目頭取・山田和彦の主導のもと再統合が果たされ、全国統一の組織として新たな一歩を踏み出します。',
        '統合は、単に組織を束ねる以上の意味を持ちました。品質基準と教育システムを全国で一つに揃えることで、どの地域でも変わらぬ執事の水準を保証する。現在の協会体制の基盤は、この時代に形づくられたものです。',
        '基盤が定まると、協会はサービスの奥行きを広げていきます。専門性の高いサービスの整備、教育プログラムの刷新、各地への展開——伝統を守りながら、静かに革新を重ねた二十年でした。',
      ],
      rows: [
        { year: '2000', label: '協会の再統合', text: '分立していた関東・関西の執事協会が再統合。二代目頭取・山田和彦の主導により、全国統一の品質基準と教育システムを確立し、現在の協会体制の基盤が整いました。' },
        { year: '2005', label: '専門サービスの拡充', text: 'プライベートな催しや法人のご要望に応える、より専門性の高いサービスを整備。多様な場面に対応できる執事の育成を進めました。' },
        { year: '2008', label: '教育プログラムの刷新', text: '執事養成プログラムに新たなカリキュラムを導入。サービスの質をいっそう高め、資格制度の充実へとつなげました。' },
        { year: '2016', label: '各地への展開', text: '主要都市でのサービス拡充とともに、各地への本格的な展開を開始。地域の特性を活かした執事文化の普及を進めました。' },
        { year: '2020', label: '非接触・オンライン対応の整備', text: '非接触型のサービスやオンラインでの支援体制を整備。培った作法を守りながら、新しい暮らしの形に柔軟に応える仕組みを築きました。' },
      ],
    },
    {
      badge: '令',
      name: '新時代への継承',
      romaji: 'A New Era',
      range: '2023 – 現在',
      image: '/images/generated/jba-fs-009-about_history-v2.webp',
      imageAlt: '新時代の執事のあり方。伝統を受け継ぎながら現代の暮らしに寄り添う姿',
      narrative: [
        '2023年、石井裕一が三代目頭取として就任。初代・田中慎吾から受け継がれてきた執事の精神を守りながら、デジタル化と国際連携を通じた「執事文化の現代化」に取り組んでいます。',
        '若い世代の育成、発信の強化、暮らしの変化への対応——受け継いだものを次の百年へと橋渡しすることが、いまの協会の使命です。2025年、協会は創立135周年を迎えました。',
      ],
      rows: [
        { year: '2023', label: '三代目頭取の就任', text: '石井裕一が三代目頭取として就任。新たなリーダーシップのもと、伝統と革新の融合による次世代の執事サービスの創造を目指しています。' },
        { year: '2024', label: '次世代育成プログラムの始動', text: '若手執事の育成に特化した新プログラムを開始。培われた技能と新しい学びの手法を組み合わせ、次代を担う執事を育てています。' },
        { year: '2025', label: '創立135周年', text: '協会は創立135周年を迎え、記念の取り組みを展開。これまでの歩みを振り返りながら、新たな未来への展望を描いています。' },
      ],
    },
  ];

  // [H1][H2][H3] 三代の頭取（継承の系譜）。年号体系に整合（初代=創設者/二代目=2000-2022/三代目=2023-現在）。
  const presidents = [
    {
      gen: '初代',
      name: '田中 慎吾',
      period: '1890年 創設者',
      description: '明治維新後の文明開化のなか、英国留学で学んだ執事文化を日本に導入し、協会を創設。西洋の伝統と日本の心を融合させた執事文化の礎を築いた、協会のはじまりの人。',
    },
    {
      gen: '二代目',
      name: '山田 和彦',
      period: '2000年 – 2022年',
      description: '二代目頭取として、分立していた関東・関西の執事協会の再統合を主導。全国統一の品質基準と教育システムを確立し、現在の協会体制の基盤を築き上げた。',
    },
    {
      gen: '三代目',
      name: '石井 裕一',
      period: '2023年 – 現在',
      description: '三代目頭取として新時代の協会運営を担う。伝統的な執事文化を継承しながら、デジタル化と国際連携を通じた「執事文化の現代化」を推進している。',
    },
  ];

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型（画像+紺オーバーレイ+左寄せテキスト）。
           由来: overview ヒーローの全幅ビルボード文法（BB-02 系）を本ページへ適用。H1文言は overview と重複させない。===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-fs-010-about_history-v2.webp"
          alt="協会の歴史を物語る資料と調度。創設から続く執事文化の系譜"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Our History — Since 1890</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">受け継がれる、</span>
              <span className="block">135年の系譜</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              1890年（明治23年）の創設以来、協会は日本における執事文化の発展に歩みを重ねてきました。西洋の伝統と日本のおもてなしの精神を融合させ、三代の頭取のもとで受け継がれてきた、135年の物語をご紹介します。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            {/* サイドナビ（共通コンポーネント・history を強調）。
                min-w-0 ラッパ: グリッド項目の min-width:auto による SP 横スクロール(824px)を抑止し、
                sidenav の横ピル列を内部スクロールに閉じ込める（AboutSideNav 本体は非編集）。 */}
            <div className="min-w-0">
              <AboutSideNav items={aboutNav} activeHref="/about/history" />
            </div>

            {/* コンテンツ列 */}
            <div className="min-w-0 space-y-16 sm:space-y-20 lg:space-y-24">

              {/* --- I. 導入リード（金eyebrow + 金差し色見出し + リード3段 + 細罫データ行）。由来: overview S1 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Prologue</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  一世紀を超えて、<span className="text-[#a38e65]">受け継がれてきたもの</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <p>
                    日本に執事という職が根づいて、135年。協会の歴史は、明治の文明開化にはじまり、戦後の全国展開、そして分立と再統合を経て、いまへと続いています。ひとつの理想が、時代ごとに形を変えながら受け継がれてきた道のりです。
                  </p>
                  <p>
                    変わったものと、変わらなかったもの——研修の手法も、暮らしの形も、時代とともに移り変わってきました。それでも、西洋式の規律と品格を日本の礼節と結び合わせるという創設の志は、三代の頭取のもとで一度も途切れることなく守られてきました。
                  </p>
                  <p>
                    このページでは、創成期から現在までの歩みを、明治・昭和・平成・令和という時代の章立てでたどります。年ごとの出来事とともに、そのとき協会が何を大切にしたのかを、あわせてお伝えします。
                  </p>
                </div>
                {/* 細罫データ行 */}
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">創設</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">歴史</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">135<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">継承</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3<span className="text-sm text-[#8f7a56] ml-1">代</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">再統合</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2000<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                </dl>
              </section>

              {/* --- II. 完全版タイムライン（主役）= 新バリエーション①
                   由来注記: overview S5「沿革ダイジェスト（単純な縦タイムライン細罫型）」を単純コピーせず、
                   本ページの主役として「時代章立て（明治/昭和/平成/令和）の完全版クロニクル」へ発展。
                   各章 = 漢字元号バッジ（紺円）+ 元号名 + 年域 + 厚い叙述 + 章画像 + 年号26px級serif強調の年表行。
                   トップ/overview に元号章立て型は無いため新造。年号強調=「一覧が見づらい」指摘対応。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Chronicle</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-4">
                  時代とともに歩んだ、<span className="text-[#a38e65]">四つの章</span>
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-12 sm:mb-14">
                  協会の135年を、明治の創成期、昭和の発展期、平成の統合と革新、そして令和の継承——四つの時代の章に分けてたどります。各章では、その時代に協会が何を大切にしたのかを叙述し、続く年表で節目の出来事をひとつずつ振り返ります。
                </p>

                <div className="space-y-16 sm:space-y-20">
                  {eras.map((era) => (
                    <article key={era.badge}>
                      {/* 章ヘッダー: 紺円の漢字バッジ + 元号名 + 英字 + 年域 */}
                      <div className="flex items-center gap-5 mb-7">
                        <span className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#1a2942] border border-[#a38e65]/50 flex items-center justify-center jba-display text-[#d7b779] text-2xl sm:text-3xl">
                          {era.badge}
                        </span>
                        <div>
                          <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.08em] mb-1">{era.romaji}</span>
                          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                            <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-tight">{era.name}</h3>
                            <span className="font-['Playfair_Display',serif] text-[#a38e65] text-base sm:text-lg tracking-[0.08em]">{era.range}</span>
                          </div>
                        </div>
                      </div>

                      {/* 章の叙述 + 章画像（画像なしセクションの連続を防ぐ・超横長を避け縦2:3級の枠は使わず高さ固定のワイド帯） */}
                      <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-start mb-9">
                        <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                          {era.narrative.map((para, i) => (
                            <p key={i}>{para}</p>
                          ))}
                        </div>
                        <div className="lg:pt-1">
                          <img
                            src={era.image}
                            alt={era.imageAlt}
                            className="w-full h-52 lg:h-[240px] object-cover border border-[#a38e65]/30"
                          />
                        </div>
                      </div>

                      {/* 年表行: 年号26px級serif強調 + 出来事名 + 説明。左に真鍮縦罫 */}
                      <div className="border-l border-[#e2ddd2] pl-6 sm:pl-8 space-y-7">
                        {era.rows.map((row) => (
                          <div key={row.year} className="relative">
                            <span className="absolute -left-[26px] sm:-left-[34px] top-2 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-6">
                              <span className="font-['Playfair_Display',serif] text-[#a38e65] text-[28px] sm:text-[32px] leading-none tracking-[0.02em] mb-2 sm:mb-0 sm:w-[92px] sm:shrink-0">{row.year}</span>
                              <div className="min-w-0">
                                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2 leading-[1.5]">{row.label}</h4>
                                <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638] leading-[1.9] max-w-2xl">{row.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              {/* --- III. 継承の系譜（三代の頭取）= 新バリエーション②
                   由来注記: overview の「番号付き2カラムブロック（S4）」と「白リッチカード」の文法を、
                   人物紹介の大組みカードへ翻案。世代ラベル（初代/二代目/三代目）+ 大きな氏名（重要情報＝
                   フォント大の指摘対応）+ 在任年 + 厚い説明。タイムライン（II）とは別型。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Lineage</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  三代にわたる、<span className="text-[#a38e65]">頭取の継承</span>
                </h2>
                <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start mb-10">
                  <div className="lg:pt-1 order-last lg:order-first">
                    <img
                      src="/images/generated/jba-fs-011-about_history-v2.webp"
                      alt="協会に受け継がれる執事の所作。世代を超えて継承される技と心"
                      className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                    協会の135年は、三人の頭取によって受け継がれてきました。創設者・田中慎吾が掲げた理想を、二代目・山田和彦が全国統一の体制へと結実させ、三代目・石井裕一が新しい時代へと橋渡しする。世代は替わっても、執事の精神は一度も途切れることなく手渡されてきました。
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                  {presidents.map((p) => (
                    <div key={p.name} className="border-t-2 border-[#1a2942] bg-[#f8f6f0] p-7 lg:p-8">
                      <span className="inline-block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.08em] mb-3">{p.gen}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-[26px] leading-tight mb-2">{p.name}</h3>
                      <p className="font-['Playfair_Display',serif] text-[#a38e65] text-lg tracking-[0.04em] mb-4">{p.period}</p>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{p.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- IV. これからの執事協会（未来への展望 + 導線）= 紺CTAパネル型。由来: overview S6 締めCTA --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. The Path Ahead</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  これからの執事協会
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    135年の歴史を礎に、協会はこれからも日本の執事文化の発展に努めてまいります。次代を担う人材の育成と、暮らしの変化に寄り添うサービスの追求を通じて、受け継いだものを次の百年へと橋渡ししていきます。執事の資格制度やサービスにご関心をお持ちの方は、あわせてご覧ください。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
                    <a href="/butler-services/" className={BTN08_ON_NAVY}>執事サービスを見る</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
